import { useQuery, useMutation, useQueryClient } from "react-query"
import { request } from "../util/request"
import { followPlaylist, unfollowPlaylist, getPlaylistUser } from "../util/backend_api/playlists"

export interface UserPlaylist {
  id: number
  name: string
  description?: string
  nada_path: string
  is_public: boolean
  playlist_posts_count: number
  user_nickname?: string
  post_id?: number
  post: {
    id?: number
    external_photo_src?: string
    cover_image_url?: string
    cover_image_url_small?: string
  }
}

type PlaylistFilterType = "created_by_user" | "followed_by_user"

const fetchUserPlaylists =
  (filterType: PlaylistFilterType) =>
  async ({ queryKey }: { queryKey: any[] }) => {
    const [, userId] = queryKey
    return request({
      queryKey: [
        "userPlaylists",
        {
          path: "/api/v1/playlists",
          params: {
            "filters[type]": filterType,
            "filters[user_id]": userId,
            per_page: 100,
          },
        },
      ],
    })
  }

export function useUserPlaylists(userId: number | undefined) {
  return useQuery(
    ["userPlaylists", userId],
    fetchUserPlaylists("created_by_user"),
    { enabled: !!userId },
  )
}

export function useFollowedPlaylists(userId: number | undefined) {
  return useQuery(
    ["followedPlaylists", userId],
    fetchUserPlaylists("followed_by_user"),
    { enabled: !!userId },
  )
}

export function usePlaylistFollow(playlistId: number | undefined, userId: number | undefined) {
  const queryClient = useQueryClient()
  const queryKey = ["playlistUser", playlistId]

  const { data, isLoading } = useQuery(
    queryKey,
    async () => {
      const result: any = await getPlaylistUser(playlistId!)
      return result?.playlist_user ?? null
    },
    { enabled: !!playlistId && !!userId },
  )

  const followMutation = useMutation(
    () => followPlaylist(playlistId!),
    {
      onSuccess: (result: any) => {
        queryClient.setQueryData(queryKey, result?.playlist_user ?? null)
        queryClient.invalidateQueries(["followedPlaylists", userId])
      },
    },
  )

  const unfollowMutation = useMutation(
    () => unfollowPlaylist(data?.id),
    {
      onSuccess: () => {
        queryClient.setQueryData(queryKey, null)
        queryClient.invalidateQueries(["followedPlaylists", userId])
      },
    },
  )

  const isFollowing = !!data
  const toggle = () => isFollowing ? unfollowMutation.mutate() : followMutation.mutate()
  const isPending = followMutation.isLoading || unfollowMutation.isLoading

  return { isFollowing, isLoading, isPending, toggle }
}
